import type { StoreReviewWorkload } from '@shared/review-workload';

export type StoreReviewSort = 'name' | 'oldest';

export function sortStoresForReview<T extends { storeId: number; storeName: string }>(
  stores: T[], workloads: Map<number, StoreReviewWorkload>, sort: StoreReviewSort,
): T[] {
  return [...stores].sort((a, b) => {
    if (sort === 'oldest') {
      const x = workloads.get(a.storeId), y = workloads.get(b.storeId);
      const xPending = (x?.pendingCount ?? 0) > 0, yPending = (y?.pendingCount ?? 0) > 0;
      if (xPending !== yPending) return xPending ? -1 : 1;
      if (xPending && yPending) {
        // Unknown-only ages cannot be ranked chronologically; keep these conspicuous
        // at the top of pending work rather than treating them as zero wait.
        const xAt = x?.oldestQueueEnteredAt ? Date.parse(x.oldestQueueEnteredAt) : null;
        const yAt = y?.oldestQueueEnteredAt ? Date.parse(y.oldestQueueEnteredAt) : null;
        if (xAt === null && yAt !== null) return -1;
        if (yAt === null && xAt !== null) return 1;
        if (xAt !== null && yAt !== null && xAt !== yAt) return xAt - yAt;
      }
    }
    return a.storeName.localeCompare(b.storeName) || a.storeId - b.storeId;
  });
}

export function storeReviewAge(workload: StoreReviewWorkload, now: number): string {
  const timestamp = workload.oldestQueueEnteredAt ? Date.parse(workload.oldestQueueEnteredAt) : NaN;
  if (!Number.isFinite(timestamp)) return 'Age unavailable';
  const minutes = Math.max(0, Math.floor((now - timestamp) / 60000));
  const hours = Math.floor(minutes / 60);
  const age = hours ? `${hours}h${minutes % 60 ? ` ${minutes % 60}m` : ''}` : `${minutes}m`;
  return `Oldest${workload.unknownAgeCount ? ' known' : ''}: ${age}`;
}

export function storeReviewHref(input: {
  retailerId: string | null; cycleId: string; storeId: number; storeName: string;
  campaignId?: string | null; taskDefinitionIds?: string | null;
}): string {
  const params = new URLSearchParams({ cycleId: input.cycleId, storeId: String(input.storeId),
    storeName: input.storeName, reviewScope: 'store' });
  if (input.retailerId) params.set('retailerId', input.retailerId);
  if (input.campaignId && input.campaignId !== 'all') params.set('campaignId', input.campaignId);
  if (input.taskDefinitionIds != null) params.set('taskDefinitionIds', input.taskDefinitionIds);
  return `/admin/activation-cockpit/review?${params}`;
}

/** A store link is an explicit pending/all-types scope, overriding saved queue filters. */
export function readStoreReviewScope(search: string) {
  const params = new URLSearchParams(search);
  const scoped = params.get('reviewScope') === 'store' && /^[1-9]\d*$/.test(params.get('storeId') ?? '');
  return { scoped, storeId: scoped ? params.get('storeId') : null,
    storeName: scoped ? params.get('storeName') : null, campaignId: params.get('campaignId') };
}
